import React, { useState, useEffect } from 'react';
import { Box, CardMedia, Skeleton } from '@mui/material';
import { useImageCache } from '../../hooks/useImageCache';

interface ProgressiveImageProps {
    src: string;
    alt: string;
    sx?: any;
    component?: 'img' | 'div';
    eager?: boolean; // If true, load immediately (above the fold)
}

/**
 * Optimized Image Component
 * 
 * Features:
 * - Browser's built-in HTTP cache (persistent across sessions)
 * - In-memory caching (fast for current session)
 * - Native lazy loading (only loads when scrolled into view)
 * - Shows skeleton while loading
 * - Handles loading errors gracefully
 */
export const ProgressiveImage: React.FC<ProgressiveImageProps> = ({
    src,
    alt,
    sx = {},
    component = 'img',
    eager = false
}) => {
    const [loaded, setLoaded] = useState(false);
    const [error, setError] = useState(false);

    // Preload and cache image in memory
    useImageCache(src);

    const handleLoad = () => {
        setLoaded(true);
    };

    const handleError = () => {
        setError(true);
        setLoaded(true);
    };

    if (error) {
        return (
            <Box
                sx={{
                    width: '100%',
                    height: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: 'grey.200',
                    color: 'grey.500',
                    borderRadius: sx.borderRadius || 1,
                    ...sx
                }}
            >
                📷
            </Box>
        );
    }

    if (component === 'div') {
        // For background images
        return (
            <Box
                sx={{
                    ...sx,
                    backgroundImage: `url(${src})`,
                    backgroundSize: sx.backgroundSize || 'cover',
                    backgroundPosition: sx.backgroundPosition || 'center',
                    backgroundRepeat: 'no-repeat',
                    opacity: loaded ? 1 : 0,
                    transition: 'opacity 0.3s ease-in-out',
                }}
            >
                {!loaded && (
                    <Skeleton
                        variant="rectangular"
                        sx={{
                            width: '100%',
                            height: '100%',
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            borderRadius: sx.borderRadius || 1,
                        }}
                    />
                )}
                <img
                    src={src}
                    alt={alt}
                    onLoad={handleLoad}
                    onError={handleError}
                    style={{ display: 'none' }}
                    loading="eager"
                />
            </Box>
        );
    }

    return (
        <>
            {!loaded && (
                <Skeleton
                    variant="rectangular"
                    sx={{
                        width: '100%',
                        height: '100%',
                        borderRadius: sx.borderRadius || 1,
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        ...sx
                    }}
                />
            )}
            <CardMedia
                component="img"
                image={src}
                alt={alt}
                onLoad={handleLoad}
                onError={handleError}
                loading={eager ? 'eager' : 'lazy'}
                sx={{
                    ...sx,
                    opacity: loaded ? 1 : 0,
                    transition: 'opacity 0.3s ease-in-out',
                }}
            />
        </>
    );
};

export default ProgressiveImage;

